import type { ReactNode } from "react";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { useEffect } from "react";
import { PhasePill, type BeaconPhase } from "./primitives/PhasePill";
import { Timer } from "./primitives/Timer";
import { usePhase } from "../state/store";

/**
 * Beacon MobileFrame — Light-Skin Mobile-Canvas mit Top-Bar (48px),
 * Phase-Pill links, optionalem Timer rechts, CTA-Footer (64px).
 *
 * Sieht auf desktop wie ein 375×812 Phone-Mockup aus, auf Mobile-Viewport
 * full-bleed.
 */

interface MobileFrameProps {
  children: ReactNode;
  /** Optional Timer-Deadline (Unix-Millis) für die Top-Bar rechts. */
  timerDeadline?: number;
  /** Footer-CTA — wenn gesetzt, wird ein 64px-Footer mit dem Inhalt
   *  gerendert (z.B. ein Submit-Button). */
  cta?: ReactNode;
  /** Override: zeige Pulse manuell. */
  livePill?: boolean;
}

const TOPBAR_HEIGHT = 48;
const CTA_HEIGHT = 64;

export function MobileFrame({
  children,
  timerDeadline,
  cta,
  livePill,
}: MobileFrameProps) {
  const { wordmark, formal } = useOffsiteConfig();
  const phase = usePhase() as BeaconPhase;

  useEffect(() => {
    document.documentElement.classList.add("skin-light");
    document.documentElement.classList.remove("skin-presenter");
  }, []);

  return (
    <div
      className="mobile-frame"
      style={{
        // Höhe und Overflow kommen aus dem Stylesheet (.mobile-frame): dort
        // lassen sich 100dvh mit Rückfall auf 100vh und das iOS-Scrollverhalten
        // setzen, was inline nicht geht. Mit `min-height: 100vh` wuchs der
        // Container über den Bildschirm hinaus — dann greift das `overflow:auto`
        // im Inhalt nicht mehr, und stattdessen scrollte die ganze Seite mitsamt
        // Kopfzeile weg.
        display: "grid",
        gridTemplateRows: `${TOPBAR_HEIGHT}px 1fr ${cta ? `${CTA_HEIGHT}px` : "0"}`,
        background: "var(--paper-50)",
        color: "var(--paper-900)",
      }}
    >
      <header
        className="mobile-frame-topbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 var(--s-4)",
          borderBottom: "1px solid var(--paper-200)",
        }}
      >
        <PhasePill phase={phase} live={livePill} formal={formal} />
        {timerDeadline ? (
          <Timer deadline={timerDeadline} size="inline" />
        ) : (
          <span
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "var(--tracking-meta)",
              textTransform: "uppercase",
              color: "var(--paper-400)",
            }}
          >
            {wordmark ?? "SVO · OFFSITE 2026"}
          </span>
        )}
      </header>

      <main
        className="mobile-frame-main"
        style={{
          padding: "var(--s-5)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--s-5)",
        }}
      >
        {children}
      </main>

      {cta && (
        <footer
          className="mobile-frame-cta"
          style={{
            display: "flex",
            alignItems: "stretch",
            background: "var(--paper-900)",
            color: "var(--svo-weiss, #fff)",
          }}
        >
          {cta}
        </footer>
      )}
    </div>
  );
}
